import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { api, eur } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useLang } from "../context/LangContext";
import { LangBadge } from "../components/shop/Flag";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const orderId = params.get("order");
  const { clear } = useCart();
  const { t } = useLang();
  const [order, setOrder] = useState(null);
  const tries = useRef(0);

  useEffect(() => {
    if (!orderId) return;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/orders/${orderId}/status`);
        setOrder(data);
        if (data.payment_status === "paid") { clear(); return; }
        if (data.payment_status !== "pending") return;
      } catch (err) { console.warn("Payment status check failed, retrying", err); }
      if (++tries.current < 15) timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [orderId, clear]);

  const paid = order?.payment_status === "paid";
  const failed = order && !["paid", "pending"].includes(order.payment_status);

  return (
    <Shell>
      {!order || (!paid && !failed) ? (
        <><Loader2 className="h-12 w-12 text-[#F2C230] animate-spin mx-auto" /><p className="mt-6 text-white/70" data-testid="payment-checking">{t("checking")}</p></>
      ) : paid ? (
        <div data-testid="payment-success">
          <CheckCircle2 className="h-14 w-14 text-[#F2C230] mx-auto" strokeWidth={1.25} />
          <h1 className="font-display text-4xl sm:text-5xl text-white mt-6">{t("success_title")}</h1>
          <p className="text-white/60 mt-4 max-w-md mx-auto">{t("success_text")}</p>
          <div className="mt-8 inline-flex flex-col gap-1 bg-white/5 border border-[#F2C230]/30 rounded-2xl px-8 py-5">
            <span className="text-[10px] uppercase tracking-[0.25em] text-white/50">{t("order_no")}</span>
            <span className="font-mono text-xl text-[#C9B6F2]" data-testid="order-number">{order.order_number}</span>
            <span className="font-mono text-sm text-white/70">{eur(order.total)}</span>
          </div>
          {order.items && <ul className="mt-6 max-w-md mx-auto text-left space-y-2" data-testid="confirmation-items">{order.items.map((i) => (
            <li key={i.sku || i.name} className="flex flex-wrap items-center gap-2 text-sm text-white/80">{i.quantity}× {i.name}<LangBadge code={i.language} dark /></li>))}</ul>}
        </div>
      ) : (
        <div data-testid="payment-failed"><XCircle className="h-14 w-14 text-white/40 mx-auto" strokeWidth={1.25} /><h1 className="font-display text-4xl text-white mt-6">{t("pay_failed")}</h1></div>
      )}
    </Shell>
  );
}

export function PaymentCancel() {
  const [params] = useSearchParams();
  const { t } = useLang();
  useEffect(() => { const o = params.get("order"); if (o) api.post(`/orders/${o}/cancel`).catch((err) => console.warn("Order cancel failed", err)); }, [params]);
  return (
    <Shell>
      <div data-testid="payment-cancelled">
        <XCircle className="h-14 w-14 text-white/40 mx-auto" strokeWidth={1.25} />
        <h1 className="font-display text-4xl sm:text-5xl text-white mt-6">{t("cancel_title")}</h1>
        <p className="text-white/60 mt-4 max-w-md mx-auto">{t("cancel_text")}</p>
      </div>
    </Shell>
  );
}

const Shell = ({ children }) => {
  const { t } = useLang();
  return (
    <div className="bg-[#120E1F] grain min-h-[70vh] grid place-items-center px-4 py-24 text-center">
      <div>{children}<div className="mt-10"><Link to="/shop" data-testid="back-to-shop-btn" className="btn-gold">{t("back_shop")}</Link></div></div>
    </div>
  );
};
